import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BILLING_QUERY_KEY } from "./useBilling";
import apiClient from "../client";
import { formatDateShort } from "../../lib/dates";
import {
  type BillingCycle,
  type TradingStrategyDto,
  TradingStrategyStatus,
} from "./useTradingStrategies";

export const MY_SUBSCRIPTIONS_QUERY_KEY = ["my-subscriptions"] as const;

// The raw status, exactly as the API serialises Status.ToString(). There is no "Cancelled" member —
// cancellation lives in cancelledAt, and the access window in activeTo.
export type SubscriptionStatus = "Unknown" | "Active" | string;

export interface CustomerTradingStrategySubscriptionDto {
  subscriptionExternalId: string;
  status: SubscriptionStatus;
  activeFrom: string;
  activeTo: string | null;
  cancelledAt: string | null;
  /** Staff cancelled it, so it's theirs to resume, not the customer's. */
  cancelledByStaff: boolean;
  billingCycle: BillingCycle | null;
  priceAtSubscriptionUsd: number | null;
  /** When it is next billed. Null when it won't be: staff-granted, or set to end. */
  renewsOn: string | null;
  pendingSwitch: CustomerTradingStrategySubscriptionPendingSwitchDto | null;
  /** The bill it's on didn't go through, and Stripe is retrying it. */
  paymentFailed: boolean;
  strategy: TradingStrategyDto;
}

/** An upgrade to yearly the customer has booked, waiting for the month she has paid for to end. */
export interface CustomerTradingStrategySubscriptionPendingSwitchDto {
  switchDate: string;
  billingCycle: BillingCycle;
  /** The yearly list price. What is taken on the switch date can be less, with an offer. */
  priceUsd: number;
  /** Stripe couldn't take the first yearly payment and is retrying it. */
  firstPaymentFailed: boolean;
}

interface ListMySubscriptionsResponse {
  items: CustomerTradingStrategySubscriptionDto[];
}

// A cart of strategies, our memberships, add-ons, or any mix.
export interface CheckoutCartRequest {
  strategies: {
    tradingStrategyId: string;
    billingCycle: BillingCycle;
    // The offer chosen for this line. Re-checked server side, so what is sent decides what is
    // asked for, never what is charged.
    offerId: string | null;
  }[];
  // Our membership tiers in the cart. Offers don't apply to them yet.
  memberships: {
    membershipTierId: string;
    billingCycle: BillingCycle;
  }[];
  // Add-ons in the cart. Offers don't apply to them, as to memberships.
  addOns: {
    addOnId: string;
    billingCycle: BillingCycle;
  }[];
}

// The checkout endpoint opens a Stripe embedded setup session and returns its client
// secret (mounts the card form) + session id (re-enters via the completion webhook).
export interface CheckoutSessionResponse {
  clientSecret: string;
  sessionId: string;
  // What Stripe says it will collect now, which is rarely the basket's total: a monthly line is
  // prorated to the next renewal and its offer with it. Null when Stripe could not be asked.
  amountDueTodayUsd: number | null;
}

// How often the post-checkout wait re-asks for the list. Entitlement rows are written off the
// invoice.paid webhook, several hops after the browser sees payment complete, so the list the
// customer lands on is legitimately empty for a few seconds and only a retry recovers it.
export const POST_CHECKOUT_POLL_INTERVAL_MS = 3000;

/**
 * The customer's subscriptions. Pass `awaitedCount` to poll: while the list holds fewer rows than
 * that, it re-fetches on an interval and stops the moment it's whole. Used by My strategies after
 * checkout — see POST_CHECKOUT_POLL_INTERVAL_MS. Omit it (every other caller) and the query never polls.
 */
export function useMySubscriptions(awaitedCount?: number) {
  return useQuery<CustomerTradingStrategySubscriptionDto[]>({
    queryKey: MY_SUBSCRIPTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListMySubscriptionsResponse>(
        "/customers/me/trading-strategy-subscriptions",
      );
      return res.data.items;
    },
    refetchInterval: (query) =>
      awaitedCount !== undefined &&
      (query.state.data?.length ?? 0) < awaitedCount
        ? POST_CHECKOUT_POLL_INTERVAL_MS
        : false,
  });
}

// No onSuccess invalidation here on purpose: this mutation succeeding means the Stripe session
// OPENED, not that anything was paid for. Refetching at that point can only ever return the
// pre-purchase list. The invalidation belongs where payment actually completes — see
// handlePaymentComplete in CheckoutWizard.
export function useCheckoutCart() {
  return useMutation({
    mutationFn: async (
      input: CheckoutCartRequest,
    ): Promise<CheckoutSessionResponse> => {
      const res = await apiClient.post<CheckoutSessionResponse>(
        "/customers/me/checkout",
        input,
      );
      return res.data;
    },
  });
}

/**
 * The card session for an upgrade, and what it will do. Nothing is charged when it opens: the yearly's
 * first bill is raised on the switch date.
 */
export interface UpgradeSessionResponse {
  clientSecret: string;
  sessionId: string;
  switchDate: string;
  /** What Stripe will take on the switch date, offer included. Null when Stripe couldn't be asked. */
  firstYearlyChargeUsd: number | null;
  /** A discount on the monthly line would still have been running on the switch date, and stops there. */
  endsMonthlyDiscount: boolean;
  /** When that discount would otherwise have ended. Null when it would have run as long as the line did. */
  monthlyDiscountEnd: string | null;
}

export interface UpgradeToYearlyInput {
  strategyId: string;
  offerId: string | null;
}

// Nothing to invalidate, as with useCheckoutCart: success means the card session opened, not that
// the upgrade was booked.
export function useUpgradeToYearly() {
  return useMutation({
    mutationFn: async ({
      strategyId,
      offerId,
    }: UpgradeToYearlyInput): Promise<UpgradeSessionResponse> => {
      const res = await apiClient.post<UpgradeSessionResponse>(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(
          strategyId,
        )}/upgrade`,
        { offerId },
      );
      return res.data;
    },
  });
}

/** The card session for an extension, and Stripe's figures for it. */
export interface ExtendSessionResponse {
  clientSecret: string;
  sessionId: string;
  /** Taken as soon as the card is saved: the new year less the unused time. */
  dueTodayUsd: number;
  newYearUsd: number;
  unusedTimeCreditUsd: number;
  /** Credit beyond the new year, left on the customer's Stripe balance. */
  leftOnAccountUsd: number;
  newPeriodEnd: string;
  replacesDiscount: boolean;
}

export interface ExtendYearInput {
  strategyId: string;
  offerId: string | null;
}

// Nothing to invalidate: success means the card session opened. Done finds out what happened after.
export function useExtendYear() {
  return useMutation({
    mutationFn: async ({ strategyId, offerId }: ExtendYearInput): Promise<ExtendSessionResponse> => {
      const res = await apiClient.post<ExtendSessionResponse>(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(strategyId)}/extend`,
        { offerId },
      );
      return res.data;
    },
  });
}

/** What extending a yearly strategy today would come to. All figures are Stripe's. */
export interface ExtensionPreviewDto {
  dueTodayUsd: number;
  newYearUsd: number;
  /** A year at the line's own price before any offer: what it renews at once a first-year offer is spent. */
  yearPriceUsd: number;
  unusedTimeCreditUsd: number;
  leftOnAccountUsd: number;
  currentPeriodEnd: string;
  newPeriodEnd: string;
  /** A discount still running on the line would stop. */
  replacesDiscount: boolean;
  /** The offer that discount came from, or null when it is none of ours. */
  runningOfferId: string | null;
  runningOfferName: string | null;
}

/**
 * Stripe's figures for extending, with the offer picked in the cart. Asked again whenever the offer
 * changes, and never cached for long: the credit shrinks by the second.
 */
export function useExtensionPreview(strategyId: string | null, offerId: string | null) {
  return useQuery<ExtensionPreviewDto>({
    queryKey: ["extension-preview", strategyId, offerId],
    queryFn: async () => {
      const res = await apiClient.get<ExtensionPreviewDto>(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(strategyId!)}/extend/preview`,
        { params: offerId === null ? {} : { offerId } },
      );
      return res.data;
    },
    enabled: strategyId !== null,
    staleTime: 0,
    retry: false,
  });
}

export type ExtensionStatus =
  | "Waiting"
  | "Extended"
  | "PaymentDeclined"
  | "NeedsAuthentication"
  | "Ended";

export interface ExtensionStatusDto {
  status: ExtensionStatus;
  /** When the year Stripe bills now ends: the new end once extended. */
  runsTo: string | null;
  /** Stripe's page for confirming the payment, only while the bank wants it confirmed. */
  paymentPageUrl: string | null;
}

const SETTLED_EXTENSION: ExtensionStatus[] = ["Extended", "PaymentDeclined", "Ended"];

/**
 * What has happened to an extension since its card was saved, asked of Stripe every few seconds until it
 * settles. Slower once Done has said it's taking a while, so a late payment still turns up. Pass null and
 * it never asks.
 */
export function useExtensionStatus(
  strategyId: string,
  sessionId: string | null,
  slow: boolean,
) {
  return useQuery<ExtensionStatusDto>({
    queryKey: ["extension-status", strategyId, sessionId],
    queryFn: async () => {
      const res = await apiClient.get<ExtensionStatusDto>(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(
          strategyId,
        )}/extend/${encodeURIComponent(sessionId!)}/status`,
      );
      return res.data;
    },
    enabled: sessionId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status !== undefined && SETTLED_EXTENSION.includes(status)) {
        return false;
      }

      return slow ? 10_000 : 3_000;
    },
  });
}

export type CheckoutStatus = "Waiting" | "PaymentDeclined";

interface CheckoutStatusDto {
  status: CheckoutStatus;
}

// A decline lands within seconds of the card being saved, so this many asks, three seconds apart,
// covers a slow queue without asking for ever.
const CHECKOUT_STATUS_ASKS = 40;

/**
 * Whether the card was declined for a checkout, asked every few seconds while a page waits on it.
 * Pass null and it never asks.
 */
export function useCheckoutStatus(sessionId: string | null) {
  return useQuery<CheckoutStatusDto>({
    queryKey: ["checkout-status", sessionId],
    queryFn: async () => {
      const res = await apiClient.get<CheckoutStatusDto>(
        `/customers/me/checkout/${encodeURIComponent(sessionId!)}/status`,
      );
      return res.data;
    },
    enabled: sessionId !== null,
    refetchInterval: (query) =>
      query.state.data?.status === "PaymentDeclined" ||
      query.state.dataUpdateCount >= CHECKOUT_STATUS_ASKS
        ? false
        : 3_000,
  });
}

/**
 * The customer's subscriptions, re-fetched until the upgrade on `strategyId` shows as waiting. Saving the
 * card finishes before the message processor books the upgrade, so for a few seconds the list doesn't
 * have it. Pass null and it never polls.
 */
export function useMySubscriptionsAwaitingUpgrade(strategyId: string | null) {
  return useQuery<CustomerTradingStrategySubscriptionDto[]>({
    queryKey: MY_SUBSCRIPTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListMySubscriptionsResponse>(
        "/customers/me/trading-strategy-subscriptions",
      );
      return res.data.items;
    },
    refetchInterval: (query) => {
      if (strategyId === null) {
        return false;
      }

      const booked = (query.state.data ?? []).some(
        (subscription) =>
          subscription.strategy.id === strategyId &&
          subscription.pendingSwitch !== null,
      );

      return booked ? false : POST_CHECKOUT_POLL_INTERVAL_MS;
    },
  });
}

/** Why they are leaving. The reason is required - declining is one of the answers. */
export interface EndSubscriptionInput {
  strategyId: string;
  reason: number;
  comment?: string | null;
}

export function useEndSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ strategyId, reason, comment }: EndSubscriptionInput) => {
      const res = await apiClient.post(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(
          strategyId,
        )}/end`,
        { reason, comment: comment?.trim() || null },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      // Ending a line changes what the next payment is, and Billing is cached until something says
      // otherwise — without this it keeps showing the old total until a hard refresh.
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

export function useResumeSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (strategyId: string) => {
      const res = await apiClient.post(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(
          strategyId,
        )}/resume`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/** Undoes a waiting upgrade: the yearly is cancelled before it has charged anything. */
export function useStayMonthly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (strategyId: string) => {
      const res = await apiClient.post(
        `/customers/me/trading-strategy-subscriptions/${encodeURIComponent(
          strategyId,
        )}/stay-monthly`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

// Still entitled: access has not lapsed. A cancelled but not yet expired subscription is still
// active — the customer keeps receiving signals until activeTo.
export function isActiveSubscription(
  subscription: Pick<
    CustomerTradingStrategySubscriptionDto,
    "status" | "activeTo"
  >,
): boolean {
  if (!subscription.activeTo) {
    return true;
  }

  return new Date(subscription.activeTo).getTime() > Date.now();
}

export function customerHasActiveSubscription(
  strategyId: string,
  subscriptions: CustomerTradingStrategySubscriptionDto[],
): boolean {
  return subscriptions.some(
    (subscription) =>
      subscription.strategy.id === strategyId &&
      isActiveSubscription(subscription),
  );
}

// What the customer should actually see, combining the two facts the API now tracks separately:
// the approval lifecycle (status) and the access window (activeTo / cancelledAt). One whose access
// was removed shows as Ended.
export type SubscriptionDisplayStatus = "Active" | "Cancelled" | "Ended";

export function deriveSubscriptionDisplayStatus(
  subscription: Pick<
    CustomerTradingStrategySubscriptionDto,
    "status" | "activeTo" | "cancelledAt"
  >,
  now: Date = new Date(),
): SubscriptionDisplayStatus {
  const hasEnded =
    subscription.activeTo !== null && new Date(subscription.activeTo) <= now;
  if (hasEnded) {
    return "Ended";
  }

  // Still entitled, but winding down. cancelledAt is the only thing that means cancelled.
  if (subscription.cancelledAt !== null) {
    return "Cancelled";
  }

  return "Active";
}

// No billing cycle / price means staff added this subscription in the Back Office (a comp), rather
// than the customer buying it through checkout. Its lifecycle belongs to staff, so the customer is
// not offered End or Resume for it.
export function isStaffManaged(
  subscription: Pick<
    CustomerTradingStrategySubscriptionDto,
    "billingCycle" | "priceAtSubscriptionUsd"
  >,
): boolean {
  return (
    subscription.billingCycle === null ||
    subscription.priceAtSubscriptionUsd === null
  );
}

// The customer's own cancellation, not yet taken effect, is the only thing they can resume.
export function canResumeSubscription(
  subscription: Pick<
    CustomerTradingStrategySubscriptionDto,
    "activeTo" | "cancelledAt" | "cancelledByStaff"
  >,
  now: Date = new Date(),
): boolean {
  return (
    subscription.cancelledAt !== null &&
    !subscription.cancelledByStaff &&
    (subscription.activeTo === null || new Date(subscription.activeTo) > now)
  );
}

// The upgrade endpoint's own cut-off: Stripe drafts the next bill around then, and a change that close
// could miss it.
const UPGRADE_CUT_OFF_MS = 24 * 60 * 60 * 1000;

/** Whether a yearly subscription's year can restart today: the refusals the extend endpoint makes. */
export function canExtendYear(
  subscription: CustomerTradingStrategySubscriptionDto,
  now: Date = new Date(),
): boolean {
  const displayStatus = deriveSubscriptionDisplayStatus(subscription, now);
  if (displayStatus !== "Active") {
    return false;
  }

  if (
    isStaffManaged(subscription) ||
    subscription.priceAtSubscriptionUsd === 0 ||
    subscription.billingCycle !== "Yearly"
  ) {
    return false;
  }

  if (subscription.strategy.status !== TradingStrategyStatus.Published) {
    return false;
  }

  // Null for one set to end, which can't be extended either.
  if (
    subscription.renewsOn === null ||
    subscription.pendingSwitch !== null ||
    subscription.paymentFailed
  ) {
    return false;
  }

  const untilRenewalMs = new Date(subscription.renewsOn).getTime() - now.getTime();

  return untilRenewalMs > UPGRADE_CUT_OFF_MS;
}

/** Whether a monthly subscription can move to yearly: the refusals the upgrade endpoint makes. */
export function canUpgradeToYearly(
  subscription: CustomerTradingStrategySubscriptionDto,
  now: Date = new Date(),
): boolean {
  const displayStatus = deriveSubscriptionDisplayStatus(subscription, now);
  if (displayStatus !== "Active") {
    return false;
  }

  if (
    isStaffManaged(subscription) ||
    subscription.priceAtSubscriptionUsd === 0 ||
    subscription.billingCycle !== "Monthly"
  ) {
    return false;
  }

  if (subscription.strategy.status !== TradingStrategyStatus.Published) {
    return false;
  }

  // Null for one set to end, which cannot move to yearly either.
  if (
    subscription.renewsOn === null ||
    subscription.pendingSwitch !== null ||
    subscription.paymentFailed
  ) {
    return false;
  }

  const untilRenewalMs = new Date(subscription.renewsOn).getTime() - now.getTime();

  return untilRenewalMs > UPGRADE_CUT_OFF_MS;
}

/**
 * When a subscription's access runs, as a fragment rather than a sentence.
 *
 * The status sits next to it on both surfaces — a pill in the card footer, a badge on the detail
 * page — so a leading "Active" says it twice. Shared so the card and the detail page date the same
 * subscription the same way.
 */
export function formatSubscriptionPeriod(
  subscription: Pick<
    CustomerTradingStrategySubscriptionDto,
    "status" | "activeFrom" | "activeTo" | "cancelledAt"
  >,
): string {
  const displayStatus = deriveSubscriptionDisplayStatus(subscription);
  const from = formatDateShort(subscription.activeFrom);

  // Still entitled but winding down, so when access stops is the useful date, not when it started.
  if (displayStatus === "Cancelled" && subscription.activeTo) {
    return `access until ${formatDateShort(subscription.activeTo)}`;
  }
  if (displayStatus === "Active") {
    return `since ${from}`;
  }
  if (subscription.activeTo) {
    return `${from} – ${formatDateShort(subscription.activeTo)}`;
  }
  return `from ${from}`;
}

// Amber for Cancelled, not red: it is still live and still resumable, so it wants a softer
// tone than Ended. Matches the Back Office indicator.
export function subscriptionStatusColor(
  displayStatus: SubscriptionDisplayStatus,
): "warning" | "success" | "secondary" {
  switch (displayStatus) {
    case "Active":
      return "success";
    case "Cancelled":
      return "warning";
    case "Ended":
      return "secondary";
  }
}
